/* =========================================================
   Berry & Blush Theme — global.js
   ---------------------------------------------------------
   Vanilla JS, keine Frameworks. Modular per Custom Elements.
   ========================================================= */

(function () {
  'use strict';

  // ---------- Helpers ----------
  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const formatMoney = (cents) => {
    return (cents / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    });
  };

  // ===========================================================
  //  CART STORE — zentrale Quelle der Wahrheit
  // ===========================================================
  const CartStore = {
    listeners: [],
    state: null,

    subscribe(fn) { this.listeners.push(fn); },
    emit() { this.listeners.forEach(fn => fn(this.state)); },

    async fetch() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      this.state = await res.json();
      this.emit();
      return this.state;
    },

    async add(items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw await res.json();
      await this.fetch();
      return this.state;
    },

    async change(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      this.state = await res.json();
      this.emit();
      return this.state;
    },

    async update(updates) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      this.state = await res.json();
      this.emit();
      return this.state;
    }
  };

  window.CartStore = CartStore;

  // ===========================================================
  //  CART DRAWER
  // ===========================================================
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = $('.cart-drawer__overlay');
      this.closeBtn = this.querySelector('[data-cart-close]');
      this.itemsEl = this.querySelector('[data-cart-items]');
      this.subtotalEl = this.querySelector('[data-cart-subtotal]');
      this.countEl = this.querySelector('[data-cart-count]');
      this.shippingBar = this.querySelector('[data-shipping-bar]');
      this.emptyEl = this.querySelector('[data-cart-empty]');
      this.contentEl = this.querySelector('[data-cart-content]');

      this.threshold = parseInt(this.dataset.shippingThreshold || '0', 10) * 100;

      this.bind();
      CartStore.subscribe(state => this.render(state));
    }

    bind() {
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

      // Trigger from anywhere
      document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-cart-open]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }
      });

      // Quantity & remove
      this.itemsEl?.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-line-change]');
        if (!btn) return;
        e.preventDefault();
        const line = parseInt(btn.dataset.line, 10);
        const qty = parseInt(btn.dataset.qty, 10);
        btn.disabled = true;
        try { await CartStore.change(line, qty); } catch (err) { console.error(err); }
      });
    }

    async open() {
      await CartStore.fetch();
      this.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('cart-drawer-open');
      this.setAttribute('aria-hidden', 'false');
    }

    close() {
      this.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');
      this.setAttribute('aria-hidden', 'true');
    }

    render(cart) {
      if (!cart) return;

      // Update count globally
      $$('[data-cart-count-global]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      // Empty state — display direkt steuern (hidden-Attribut wird von inline style:flex überschrieben)
      if (cart.item_count === 0) {
        if (this.emptyEl)   this.emptyEl.style.display   = 'flex';
        if (this.contentEl) this.contentEl.style.display = 'none';
        return;
      }
      if (this.emptyEl)   this.emptyEl.style.display   = 'none';
      if (this.contentEl) this.contentEl.style.display = 'flex';

      // Items
      if (this.itemsEl) {
        this.itemsEl.innerHTML = cart.items.map((item, idx) => `
          <li class="cart-item" data-line="${idx + 1}">
            <a href="${item.url}" class="cart-item__media">
              <img src="${item.image ? item.image.replace('.jpg', '_120x.jpg').replace('.png', '_120x.png').replace('.webp', '_120x.webp') : ''}"
                   alt="${item.product_title}" loading="lazy" width="80" height="100">
            </a>
            <div class="cart-item__info">
              <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
              <div class="cart-item__price">${formatMoney(item.final_price)}</div>
              <div class="cart-item__controls">
                <div class="qty-selector">
                  <button data-line-change data-line="${idx + 1}" data-qty="${item.quantity - 1}" aria-label="Menge verringern">−</button>
                  <span>${item.quantity}</span>
                  <button data-line-change data-line="${idx + 1}" data-qty="${item.quantity + 1}" aria-label="Menge erhöhen">+</button>
                </div>
                <button data-line-change data-line="${idx + 1}" data-qty="0" class="cart-item__remove" aria-label="Entfernen">
                  Entfernen
                </button>
              </div>
            </div>
          </li>
        `).join('');
      }

      // Subtotal
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);

      // Shipping progress bar
      if (this.shippingBar && this.threshold > 0) {
        const remaining = Math.max(0, this.threshold - cart.total_price);
        const progress = Math.min(100, (cart.total_price / this.threshold) * 100);
        const fill = this.shippingBar.querySelector('[data-shipping-fill]');
        const text = this.shippingBar.querySelector('[data-shipping-text]');
        if (fill) fill.style.width = `${progress}%`;
        if (text) {
          text.innerHTML = remaining === 0
            ? '🎉 Du hast kostenlosen Versand erreicht!'
            : `Nur noch <strong>${formatMoney(remaining)}</strong> bis zum kostenlosen Versand`;
        }
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // ===========================================================
  //  PRODUCT FORM — Add to Cart
  // ===========================================================
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');

      this.form?.addEventListener('submit', e => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.submitBtn.disabled) return;

      this.submitBtn.disabled = true;
      const originalText = this.submitBtn.querySelector('span').textContent;
      this.submitBtn.querySelector('span').textContent = 'Wird hinzugefügt...';
      this.errorEl?.setAttribute('hidden', '');

      const formData = new FormData(this.form);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || err.message || 'Fehler beim Hinzufügen');
        }
        await CartStore.fetch();
        $('cart-drawer')?.open();
        this.submitBtn.querySelector('span').textContent = '✓ Hinzugefügt';
        setTimeout(() => {
          this.submitBtn.querySelector('span').textContent = originalText;
          this.submitBtn.disabled = false;
        }, 1500);
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.textContent = err.message;
          this.errorEl.removeAttribute('hidden');
        }
        this.submitBtn.querySelector('span').textContent = originalText;
        this.submitBtn.disabled = false;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  // ===========================================================
  //  VARIANT SELECTOR
  // ===========================================================
  class VariantSelector extends HTMLElement {
    constructor() {
      super();
      this.productJson = JSON.parse(this.querySelector('[data-product-json]').textContent);

      // Das Form-Input liegt im product-form, NICHT im variant-selector selbst
      // Deshalb vom nächsten gemeinsamen Elternelement aus suchen
      const productSection = this.closest('.product__info') || document;
      this.variantInput    = productSection.querySelector('[name="id"]');
      this.priceEl         = productSection.querySelector('[data-product-price]');
      this.comparePriceEl  = productSection.querySelector('[data-product-compare-price]');
      this.submitBtn       = productSection.querySelector('[data-add-to-cart]');
      this.skuEl           = productSection.querySelector('[data-product-sku]');

      // Galerie-Elemente
      this.mainImg         = document.querySelector('[data-product-featured-image]');
      this.thumbBtns       = Array.from(document.querySelectorAll('[data-gallery-thumb]'));

      this.bind();
    }

    bind() {
      this.querySelectorAll('input[type="radio"], select').forEach(el => {
        el.addEventListener('change', () => this.update());
      });
    }

    getSelectedOptions() {
      const options = [];
      this.querySelectorAll('[data-option-position]').forEach(group => {
        const pos = parseInt(group.dataset.optionPosition, 10);
        const checked = group.querySelector('input:checked') || group.querySelector('select');
        if (checked) options[pos - 1] = checked.value;
      });
      return options;
    }

    // Prüft für jede Option ob es eine verfügbare Variante gibt,
    // die diese Option in Kombination mit den anderen gewählten Optionen enthält
    updateAvailability(currentSelected) {
      this.querySelectorAll('[data-option-position]').forEach(group => {
        const pos = parseInt(group.dataset.optionPosition, 10) - 1;

        group.querySelectorAll('input[type="radio"]').forEach(input => {
          // Teste-Kombination: diese Option + alle anderen aktuell gewählten
          const testOptions = [...currentSelected];
          testOptions[pos] = input.value;

          // Gibt es eine verfügbare Variante für diese Kombination?
          const available = this.productJson.variants.some(v =>
            v.available &&
            testOptions.every((opt, i) => opt === undefined || v.options[i] === opt)
          );

          input.disabled = !available;
        });
      });
    }

    update() {
      const selected = this.getSelectedOptions();
      const variant = this.productJson.variants.find(v =>
        v.options.every((opt, i) => opt === selected[i])
      );
      if (!variant) return;

      // --- Varianten-ID ins versteckte Form-Feld schreiben ---
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // --- Option-Label (ausgewählter Wert sichtbar) ---
      this.querySelectorAll('[data-option-position]').forEach(group => {
        const labelEl = group.querySelector('[data-option-selected]');
        const checked = group.querySelector('input:checked');
        if (labelEl && checked) labelEl.textContent = checked.value;
      });

      // --- Preis ---
      if (this.priceEl) this.priceEl.textContent = formatMoney(variant.price);
      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }

      // --- SKU ---
      if (this.skuEl) this.skuEl.textContent = variant.sku || '';

      // --- Add-to-Cart Button ---
      if (this.submitBtn) {
        const span = this.submitBtn.querySelector('span');
        this.submitBtn.disabled = !variant.available;
        if (span) span.textContent = variant.available ? 'In den Warenkorb' : 'Ausverkauft';
      }

      // --- Sticky ATC ebenfalls aktualisieren ---
      const stickyPrice = document.querySelector('.sticky-atc__price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      // --- URL aktualisieren ---
      if (window.history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      // --- Bild aktualisieren ---
      if (variant.featured_image && this.mainImg) {
        const imgSrc = variant.featured_image.src;
        const variantMediaId = variant.featured_image.id;
        const src900  = imgSrc.replace(/(\.[a-z]+)(\?.*)?$/, '_900x$1');
        const src600  = imgSrc.replace(/(\.[a-z]+)(\?.*)?$/, '_600x$1');
        const src1400 = imgSrc.replace(/(\.[a-z]+)(\?.*)?$/, '_1400x$1');

        this.mainImg.src    = src900;
        this.mainImg.srcset = `${src600} 600w, ${src900} 900w, ${src1400} 1400w`;

        this.thumbBtns.forEach(btn => {
          const matches = btn.dataset.mediaId == variantMediaId;
          btn.classList.toggle('is-active', matches);
        });
      }

      // --- Verfügbarkeit aller Optionen aktualisieren ---
      this.updateAvailability(selected);
    }

    connectedCallback() {
      // Beim ersten Laden sofort Verfügbarkeit prüfen
      const initial = this.getSelectedOptions();
      this.updateAvailability(initial);
    }
  }
  customElements.define('variant-selector', VariantSelector);

  // ===========================================================
  //  PREDICTIVE SEARCH
  // ===========================================================
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.closeBtn = this.querySelector('[data-search-close]');
      this.limit = parseInt(this.dataset.limit || '5', 10);
      this.showImages = this.dataset.showImages === 'true';

      this.bind();
    }

    bind() {
      this.input?.addEventListener('input', debounce(() => this.search(), 250));
      this.closeBtn?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

      document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-search-open]');
        if (trigger) { e.preventDefault(); this.open(); }
      });
    }

    open() {
      this.classList.add('is-open');
      setTimeout(() => this.input?.focus(), 100);
    }

    close() {
      this.classList.remove('is-open');
      if (this.input) this.input.value = '';
      if (this.results) this.results.innerHTML = '';
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=${this.limit}&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`);
        const data = await res.json();
        this.render(data.resources.results.products || [], q);
      } catch (err) {
        console.error(err);
      }
    }

    render(products, query) {
      if (products.length === 0) {
        this.results.innerHTML = `<p class="predictive-search__empty">Keine Treffer für „${query}"</p>`;
        return;
      }
      this.results.innerHTML = `
        <ul class="predictive-search__list">
          ${products.map(p => `
            <li>
              <a href="${p.url}" class="predictive-search__item">
                ${this.showImages && p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy" width="60" height="60">` : ''}
                <span class="predictive-search__item-info">
                  <span class="predictive-search__item-title">${p.title}</span>
                  <span class="predictive-search__item-price">${p.price}</span>
                </span>
              </a>
            </li>
          `).join('')}
        </ul>
        <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all">Alle Ergebnisse für „${query}" ansehen →</a>
      `;
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  // ===========================================================
  //  MOBILE MENU DRAWER
  // ===========================================================
  class MenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('.menu-drawer__overlay');
      this.closeBtn = this.querySelector('[data-menu-close]');

      this.bind();
    }

    bind() {
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-menu-open]');
        if (trigger) { e.preventDefault(); this.open(); }
      });

      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  // ===========================================================
  //  PRODUCT GALLERY (Produktseite)
  // ===========================================================
  class ProductGallery extends HTMLElement {
    constructor() {
      super();
      this.mainImg = this.querySelector('[data-gallery-main]');
      this.thumbs = $$('[data-gallery-thumb]', this);

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', e => {
          e.preventDefault();
          this.select(i);
        });
      });
    }

    select(index) {
      const thumb = this.thumbs[index];
      if (!thumb || !this.mainImg) return;
      this.mainImg.src = thumb.dataset.imageSrc;
      this.mainImg.srcset = thumb.dataset.imageSrcset || '';
      this.thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  }
  customElements.define('product-gallery', ProductGallery);

  // ===========================================================
  //  WISHLIST (localStorage für Gäste, Metafields für Logged-in)
  // ===========================================================
  const Wishlist = {
    KEY: 'bb_wishlist',
    get() {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); }
      catch { return []; }
    },
    set(arr) {
      localStorage.setItem(this.KEY, JSON.stringify(arr));
      this.updateCount();
      this.updateButtons();
    },
    has(handle) { return this.get().includes(handle); },
    add(handle) {
      const list = this.get();
      if (!list.includes(handle)) { list.push(handle); this.set(list); }
    },
    remove(handle) {
      this.set(this.get().filter(h => h !== handle));
    },
    toggle(handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
    },
    updateCount() {
      const count = this.get().length;
      $$('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },
    updateButtons() {
      $$('[data-wishlist-toggle]').forEach(btn => {
        const handle = btn.dataset.wishlistToggle;
        btn.classList.toggle('is-active', this.has(handle));
        btn.setAttribute('aria-pressed', this.has(handle));
      });
    }
  };
  window.Wishlist = Wishlist;

  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    Wishlist.toggle(btn.dataset.wishlistToggle);
  });

  // ===========================================================
  //  QUICK ADD
  // ===========================================================
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.quickAdd;
    btn.disabled = true;
    try {
      await CartStore.add([{ id: variantId, quantity: 1 }]);
      $('cart-drawer')?.open();
    } catch (err) {
      console.error(err);
    } finally {
      btn.disabled = false;
    }
  });

  // ===========================================================
  //  COLLECTION FILTER & SORT
  // ===========================================================
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.form?.addEventListener('change', debounce(() => this.submit(), 300));
    }

    async submit() {
      const formData = new FormData(this.form);
      const params = new URLSearchParams(formData).toString();
      const url = `${window.location.pathname}?${params}`;
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-product-grid]');
      const oldGrid = $('[data-product-grid]');
      if (newGrid && oldGrid) oldGrid.innerHTML = newGrid.innerHTML;
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  // ===========================================================
  //  STICKY ADD TO CART (Mobile, Produktseite)
  // ===========================================================
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.trigger = $('[data-sticky-atc-trigger]');
      if (!this.trigger) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { rootMargin: '-200px 0px 0px 0px' });

      observer.observe(this.trigger);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  // ===========================================================
  //  ACCORDION (FAQ)
  // ===========================================================
  document.addEventListener('click', e => {
    const summary = e.target.closest('.accordion__summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (details.tagName === 'DETAILS') {
      // native — let it happen
    }
  });

  // ===========================================================
  //  INIT
  // ===========================================================
  document.addEventListener('DOMContentLoaded', () => {
    CartStore.fetch();
    Wishlist.updateCount();
    Wishlist.updateButtons();
  });

})();
